import { useEffect } from "react";
import { FilterTab } from "../../../components/Filters";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../../store/state/SwitchTabs";
import { DataTabs } from "./Data";
import { setViewMode } from "../../../store/state/ViewMode";

interface DataTab {
  label: string;
  value?: string;
}

interface SecondaryHeaderProps {
  showViewMode?: boolean;
  // Your additions
  customDataTabs?: DataTab[];
  onViewModeChange?: (tab: DataTab | { label: string }) => void;
  // Incoming teammate additions (kept intact)
  ExchangeDataTabs?: { label: string; value?: string }[];
}

const SecondaryHeader = ({
  showViewMode = true,
  customDataTabs,       // Yours
  onViewModeChange,     // Yours
  ExchangeDataTabs,     // Teammate's
}: SecondaryHeaderProps) => {
  const dispatch = useDispatch();
  const activeViewMode = useSelector((state: any) => state.viewMode.activeView);
  const tabNames = useSelector((state: any) => state.switchTabs.tabNames);
  const showTabs = useSelector((state: any) => state.switchTabs.showTabs);
  const selectedTab = useSelector((state: any) => state.switchTabs.activeTab);
  const disabledTabs = useSelector(
    (state: any) => state.switchTabs.disabledTabs || [],
  );

  // Safely combine the logic: 
  // 1. Use your custom tabs if provided
  // 2. Otherwise use teammate's exchange tabs if provided
  // 3. Fallback to default DataTabs
  const activeTabs = customDataTabs || ExchangeDataTabs || DataTabs;

  const handleTabChange = (option: string) => {
    // Check if the tab is disabled
    if (disabledTabs.includes(option)) {
      return; // Do nothing if tab is disabled
    }

    if (option === "Restricted demand") return;
    if (option === "Restricted demand Trend Analysis") return;
    if (option === "0verview") return;
    if (option === "Trend analysis") return;
    if (option === "collective") return;

    dispatch(setActiveTab(option));
    const url = new URL(window.location.href);
    url.searchParams.set("tab", option);
    window.history.pushState({}, "", url);
  };

  useEffect(() => {
    // Update selected tab if URL changes
    const handlePopState = () => {
      const tab = new URLSearchParams(window.location.search).get("tab");
      if (tab && tabNames.includes(tab)) {
        dispatch(setActiveTab(tab));
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dispatch, tabNames]);

  // Merged useEffect to handle setting the initial view mode for ANY of the 3 tab sources
  useEffect(() => {
    if (activeTabs && activeTabs.length > 0) {
      dispatch(setViewMode(activeTabs[0]));
    }
  }, [dispatch, customDataTabs, ExchangeDataTabs]); // Watches both prop sources

  return (
    <div className="bg-white p-[0.5rem] rounded-xl flex flex-wrap gap-3 justify-between items-center shadow-sm">
      <>
        <div>
          {showViewMode && (
            <div>
              {/* By using 'activeTabs' here, we safely handle your custom tabs, 
                their exchange tabs, and the default tabs without needing a messy if/else block.
              */}
              <FilterTab
                options={activeTabs.map((item) => item.label)}
                selected={[activeViewMode?.label]}
                onClick={(option, index) => {
                  const tab = activeTabs[index];
                  dispatch(setViewMode(tab));
                  // Your callback safely fires here
                  onViewModeChange?.(tab);
                }}
                variant="pill-outline" // Teammate's UI changes kept intact
              />
            </div>
          )}
        </div>

        {/* Teammate's new logic for showing/hiding tabs safely kept below */}
        {showTabs ? (
          tabNames.length === 1 ? (
            <div className="inline-flex items-center gap-1 bg-gray-100 p-[1px] rounded-full shadow-inner">
              <div className="tab-pill tab-pill-filled-active">
                {tabNames[0]}
              </div>
            </div>
          ) : (
            <FilterTab
              options={tabNames}
              selected={[selectedTab]}
              onClick={handleTabChange}
              disabledOptions={disabledTabs}
              variant="pill-filled"
            />
          )
        ) : (
          <div className="w-40"></div>
        )}
      </>
    </div>
  );
};

export default SecondaryHeader;